import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Send, Heart, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRAYER_IMG = "https://images.pexels.com/photos/5875069/pexels-photo-5875069.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const PrayerRequest = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", request_type: "general", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/prayer-requests`, form);
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", request_type: "general", message: "" });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      console.error("Failed to submit prayer request:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="prayer" data-testid="prayer-section" className="py-20 md:py-32 bg-white relative overflow-hidden">
      <div className="bubble-decoration bg-red-300 w-64 h-64 -top-32 -left-32 animate-pulse-soft" />
      <div className="bubble-decoration bg-amber-300 w-48 h-48 bottom-0 right-0 animate-pulse-soft" style={{ animationDelay: "1.5s" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image side */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7 }}
            className="hidden lg:block relative"
          >
            <div className="rounded-[2.5rem] overflow-hidden shadow-[0_20px_60px_rgb(0,0,0,0.08)]">
              <img src={PRAYER_IMG} alt="Person praying" className="w-full h-[520px] object-cover" />
            </div>
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-5 -right-5 bg-amber-500 text-white rounded-2xl p-5 shadow-xl"
            >
              <Heart size={28} fill="white" />
              <p className="text-sm font-semibold mt-1">We pray for you</p>
            </motion.div>
          </motion.div>

          {/* Form side */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <span className="font-accent text-2xl text-amber-600">We Care</span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-stone-900 mt-2">
              Prayer <span className="text-red-600">Request</span>
            </h2>
            <p className="text-base text-stone-600 mt-4 mb-8">
              Share your prayer needs with us. Our prayer team will lift you up in prayer.
              Your request is kept confidential and handled with care.
            </p>

            {submitted ? (
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center"
                data-testid="prayer-success-message"
              >
                <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                <h3 className="text-xl font-bold text-green-800">Prayer Request Submitted</h3>
                <p className="text-green-600 mt-2">Our team will be praying for you. God bless you.</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="prayer-request-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">Your Name *</label>
                    <Input
                      data-testid="prayer-name-input"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Enter your name"
                      className="rounded-full border-stone-200 bg-stone-50 focus:ring-2 focus:ring-red-200 focus:border-red-400 px-5 py-3 h-auto"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">Email</label>
                    <Input
                      data-testid="prayer-email-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="your@email.com"
                      className="rounded-full border-stone-200 bg-stone-50 focus:ring-2 focus:ring-red-200 focus:border-red-400 px-5 py-3 h-auto"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">Phone</label>
                    <Input
                      data-testid="prayer-phone-input"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="Phone number"
                      className="rounded-full border-stone-200 bg-stone-50 focus:ring-2 focus:ring-red-200 focus:border-red-400 px-5 py-3 h-auto"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-stone-700 mb-1.5 block">Request Type</label>
                    <Select
                      value={form.request_type}
                      onValueChange={(v) => setForm({ ...form, request_type: v })}
                    >
                      <SelectTrigger
                        data-testid="prayer-type-select"
                        className="rounded-full border-stone-200 bg-stone-50 focus:ring-2 focus:ring-red-200 focus:border-red-400 px-5 py-3 h-auto"
                      >
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="rounded-2xl">
                        <SelectItem value="general">General Prayer</SelectItem>
                        <SelectItem value="healing">Healing</SelectItem>
                        <SelectItem value="family">Family</SelectItem>
                        <SelectItem value="financial">Financial Breakthrough</SelectItem>
                        <SelectItem value="guidance">Guidance & Direction</SelectItem>
                        <SelectItem value="thanksgiving">Thanksgiving</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">Your Prayer Request *</label>
                  <Textarea
                    data-testid="prayer-message-textarea"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Share your prayer need with us..."
                    rows={5}
                    className="rounded-2xl border-stone-200 bg-stone-50 focus:ring-2 focus:ring-red-200 focus:border-red-400 px-5 py-3 resize-none"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="prayer-submit-btn"
                  className="w-full bg-red-600 text-white hover:bg-red-700 rounded-full px-8 py-4 font-semibold shadow-lg hover:shadow-xl transition-transform duration-200 hover:-translate-y-1 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? (
                    <span>Submitting...</span>
                  ) : (
                    <>
                      <Send size={18} />
                      Submit Prayer Request
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
